import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ComputedFrom, splitObservable } from '@certemy/common';
import { PageAction, SmartTable } from '@certemy/ui-components';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject } from 'rxjs';
import { map, skip, switchMap, take } from 'rxjs/operators';
import { NgbCustomModalComponent } from 'components/modal-windows/ngb-custom-modal/ngb-custom-modal.component';
import { DEFAULT_MODAL_OPTIONS } from 'constants/constants';
import { getSection, pageToFilterStruct } from 'utils/helpers';
import { AssignmentsService } from 'services/assignments/assignments.service';
import {
  Assignment,
  AssignmentListQueryParam,
  AssignmentListResponse,
  AssignmentsListFilters,
} from 'services/assignments/assignments.interface';
import { WORKFLOW_MODULE_ID } from 'constants/workflow.constants';
import {
  IPermissions,
  PolicyObj,
  PolicyPermissionsService,
} from 'services/policy-permission/policy-permissions.service';
import { Selectors as ProfileSelectors } from 'features/profiles-state/+state/profiles-state.state';
import { ROLE_CODES } from 'constants/role.constants';
import { Store } from '@ngrx/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

const ASSIGNMENTS_LIST_POLICIES: Record<string, PolicyObj> = {
  addCredentialingAssignment: {
    name: 'addCredentialingAssignment',
    url: 'credentialing/assignments/list',
    action: 'add',
  },
  addTestingWorkflowAssignment: {
    name: 'addTestingWorkflowAssignment',
    url: 'testing/assignments/list',
    action: 'add',
  },
  addComplianceWorkflowAssignment: {
    name: 'addComplianceWorkflowAssignment',
    url: 'compliance/assignments/list',
    action: 'add',
  },
};

interface AssignmentsListPolicies extends IPermissions {
  addCredentialingAssignment: boolean;
  addTestingWorkflowAssignment: boolean;
  addComplianceWorkflowAssignment: boolean;
}

@UntilDestroy()
@Component({
  selector: 'cm-assignments-list',
  templateUrl: './assignments-list.component.html',
  styleUrls: ['./assignments-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentsListComponent implements OnInit, OnDestroy {
  curSection = getSection(window.location.pathname);

  assignmentsListData: AssignmentListResponse;
  tableData: AssignmentListResponse;
  workflowTypeId: number;
  filters$: BehaviorSubject<AssignmentsListFilters>;
  ADD_ASSIGNMENT = 'ADD_ASSIGNMENT';
  currentRoleId: ROLE_CODES = null;
  tableHeaders: SmartTable.Header[] = [
    { name: 'Name', value: 'name', type: SmartTable.ColumnTypeId.STRING, sortable: true },
    { name: 'Description', value: 'description', type: SmartTable.ColumnTypeId.STRING },
    { name: 'Items', value: 'certificateCount', type: SmartTable.ColumnTypeId.STRING },
    { name: 'Assigned', value: 'userCount', type: SmartTable.ColumnTypeId.STRING },
    { name: 'Updated', value: 'creationDate', type: SmartTable.ColumnTypeId.DATE, sortable: true },
  ];

  policyPermissions: AssignmentsListPolicies = {
    addCredentialingAssignment: false,
    addTestingWorkflowAssignment: false,
    addComplianceWorkflowAssignment: false,
  };

  @ComputedFrom('policyPermissions', 'workflowTypeId', 'currentRoleId')
  get availableActions() {
    const actions: PageAction[] = [];
    if (
      this.currentRoleId !== ROLE_CODES.IMPOSER &&
      ((WORKFLOW_MODULE_ID.CREDENTIAL === this.workflowTypeId && this.policyPermissions.addCredentialingAssignment) ||
        (WORKFLOW_MODULE_ID.TESTING_WORKFLOW === this.workflowTypeId &&
          this.policyPermissions.addTestingWorkflowAssignment) ||
        (WORKFLOW_MODULE_ID.COMPLIANCE === this.workflowTypeId &&
          this.policyPermissions.addComplianceWorkflowAssignment))
    ) {
      actions.push({ type: this.ADD_ASSIGNMENT, name: '+ Add assignment' });
    }
    return actions;
  }

  get title() {
    switch (this.workflowTypeId) {
      case WORKFLOW_MODULE_ID.TESTING_WORKFLOW:
        return 'Testing Assignments';
      case WORKFLOW_MODULE_ID.COMPLIANCE:
        return 'Compliance Assignments';
      default:
        return 'Assignments';
    }
  }

  constructor(
    private router: Router,
    private assignmentsService: AssignmentsService,
    private modalService: NgbModal,
    private activateRoute: ActivatedRoute,
    private changeDetector: ChangeDetectorRef,
    private policyPermissionsService: PolicyPermissionsService,
    private store: Store<any>,
  ) {}

  ngOnInit() {
    this.activateRoute.data.subscribe((data) => {
      this.tableData = data.assignmentsData;
      this.workflowTypeId = Number(this.activateRoute.snapshot.params.workflowTypeId);
      this.changeDetector.markForCheck();
    });
    this.initFilters();

    this.store
      .select(ProfileSelectors.currentRoleId)
      .pipe(take(1))
      .subscribe((roleId: ROLE_CODES) => {
        this.currentRoleId = roleId;
      });

    this.policyPermissionsService
      .hasMultiplePermissions(Object.values(ASSIGNMENTS_LIST_POLICIES))
      .pipe(take(1))
      .subscribe((policyPermissions: AssignmentsListPolicies) => {
        this.policyPermissions = policyPermissions;
        this.changeDetector.detectChanges();
      });
  }

  ngOnDestroy() {}

  private initFilters() {
    const baseFilterParam = new AssignmentsListFilters();
    const activeHeader = this.tableHeaders.find((header: SmartTable.Header) => {
      const value = header.propertyName || header.value;
      return value === baseFilterParam.orderId;
    });
    const defaultFilter = {
      ...baseFilterParam,
      order: { header: activeHeader, order: baseFilterParam.orderType },
      filters: {
        workflowTypeId: [this.workflowTypeId],
      },
    };
    this.filters$ = new BehaviorSubject(defaultFilter);

    this.filters$
      .pipe(
        skip(1),
        map((nextfilters: AssignmentsListFilters) => {
          const { page, search, orderId, orderType } = nextfilters;

          return {
            search,
            orderId,
            orderType,
            filters: { workflowTypeId: [this.workflowTypeId] },
            ...pageToFilterStruct(page),
          };
        }),
        switchMap((filters: AssignmentListQueryParam) => {
          return this.assignmentsService.getAssignmentsList(filters);
        }),
        untilDestroyed(this),
      )
      .subscribe((tableData: AssignmentListResponse) => {
        this.tableData = tableData;
        this.changeDetector.markForCheck();
      });
  }

  onRowClick(assignment: Assignment) {
    this.router.navigate([`/${this.curSection}/assignments/${this.workflowTypeId}/${assignment.id}`]);
  }

  createAssignment() {
    this.router.navigateByUrl(`/${this.curSection}/assignments/${this.workflowTypeId}/new`);
  }

  private showMissingMappingModal() {
    const modal = this.modalService.open(NgbCustomModalComponent, DEFAULT_MODAL_OPTIONS);
    modal.componentInstance.config = {
      title: 'No School Code for Your Organization',
      body: 'There is no School Code that corresponds to your Organization in system. Please contact your Administrator.',
      defaultButtonText: 'Close',
    };
  }

  onPageAction(event) {
    if (event === this.ADD_ASSIGNMENT) {
      this.createAssignment();
    }
  }

  onOrderChange(orderedHeader: SmartTable.OrderedHeader) {
    this.nextFilters((filters) => ({
      ...filters,
      page: 1,
      orderId: orderedHeader.header.propertyName || orderedHeader.header.value,
      orderType: orderedHeader.order,
      order: orderedHeader,
    }));
  }

  onSearchChange(search: string) {
    this.nextFilters((filters) => ({
      ...filters,
      page: 1,
      search,
    }));
  }

  onPageChange(page: number) {
    this.nextFilters((filters) => ({
      ...filters,
      page,
    }));
  }

  private nextFilters(reduce: (filters: AssignmentsListFilters) => AssignmentsListFilters) {
    const currentFilters = this.filters$.getValue();

    this.filters$.next(reduce(currentFilters));
  }
}
